import { useMemo } from 'react';
import { api } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import type { LeaveRow, FollowUpRow } from '@/lib/types';
import { initials, formatDate } from '@/lib/format';

function daysBetween(a: string, b: string): number {
  const d1 = new Date(a).getTime();
  const d2 = new Date(b).getTime();
  if (Number.isNaN(d1) || Number.isNaN(d2)) return 0;
  return Math.max(1, Math.round((d2 - d1) / 86_400_000) + 1);
}

export default function BottomPanels() {
  const { data: leavesData, loading: leaveLoading, reload: reloadLeaves } = useApiData<LeaveRow[]>(() => api.leaves());
  const { data: followUps, loading: followLoading } = useApiData<FollowUpRow[]>(() => api.followUpsToday());

  const pending = useMemo(() => (leavesData ?? []).filter((l) => l.status === 'pending'), [leavesData]);

  const decide = async (id: number, status: 'approved' | 'rejected') => {
    try { await api.updateLeave(id, { status }); reloadLeaves(); } catch { /* ignore */ }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Pending Leave Requests */}
      <div className="bg-white rounded-xl border border-[#E1E5EA] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E1E5EA]">
          <h3 className="text-sm font-semibold text-[#3C3C3C]">Pending Leave Requests</h3>
          <span className="text-xs bg-red-100 text-red-600 font-semibold px-2 py-0.5 rounded-full">{pending.length} pending</span>
        </div>
        <div className="divide-y divide-gray-50">
          {leaveLoading ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
          ) : pending.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">No pending requests</div>
          ) : pending.map((req) => (
            <div key={req.id} className="flex items-center gap-3 px-5 py-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0B6E77] text-white text-xs font-bold flex-shrink-0">
                {initials(req.employeeName || req.employeeCode || '?')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{req.employeeName || req.employeeCode || 'Employee'}</p>
                <p className="text-xs text-gray-400 truncate">{formatDate(req.startsOn)}–{formatDate(req.endsOn)} · {daysBetween(req.startsOn, req.endsOn)}d · {req.leaveType || req.reason || 'Leave'}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => decide(req.id, 'approved')} className="px-2.5 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors cursor-pointer whitespace-nowrap">Approve</button>
                <button onClick={() => decide(req.id, 'rejected')} className="px-2.5 py-1 text-xs font-semibold bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors cursor-pointer whitespace-nowrap">Reject</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Follow-ups Due Today */}
      <div className="bg-white rounded-xl border border-[#E1E5EA] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E1E5EA]">
          <h3 className="text-sm font-semibold text-[#3C3C3C]">Follow-ups Due Today</h3>
          <span className="text-xs bg-amber-100 text-amber-600 font-semibold px-2 py-0.5 rounded-full">{(followUps ?? []).length} due</span>
        </div>
        <div className="divide-y divide-gray-50">
          {followLoading ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
          ) : (followUps ?? []).length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">No follow-ups due today</div>
          ) : (followUps ?? []).map((item) => (
            <div key={item.id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex-shrink-0">
                {initials(item.name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                <p className="text-xs text-gray-400 truncate">{item.counselorName || 'Unassigned'} · {item.status}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
